import { Link } from "react-router-dom";
import { PageHeader, Panel, StatusBadge } from "@/components/app/common";
import { BusinessPipeline } from "@/components/dashboard/BusinessPipeline";
import { QuotePipeline } from "@/components/dashboard/QuotePipeline";
import { useMe } from "@/components/app/MeContext";
import { useFetch } from "@/hooks/useFetch";
import { fmtDateTime, inr0 as inr } from "@/lib/format";

const Stat = ({ k, v, sub, to, tone = "text-slate-900", testid }) => (
  <Link to={to} className="bg-white border border-slate-200 rounded-md p-4 hover:border-sky-400 transition-colors" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div>
    <div className={`text-2xl font-semibold font-mono mt-1 ${tone}`}>{v}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </Link>
);
const lms = (n) => `${n} LM${n === 1 ? "" : "s"}`;

export default function Dashboard() {
  const { can } = useMe();
  if (!can("dashboard.view")) return (
    <div data-testid="dashboard-restricted">
      <PageHeader title="Welcome" subtitle="The business dashboard is shown only to people an admin has given dashboard access." />
      <div className="flex flex-wrap gap-3">
        {can("orders.view") && <Link to="/orders" className="text-sm text-sky-700 hover:underline" data-testid="restricted-orders-link">Open the LM Register</Link>}
        {can("logistics.view") && <Link to="/logistics" className="text-sm text-sky-700 hover:underline">Dispatch & Logistics</Link>}
      </div>
    </div>
  );
  return <DashboardBody />;
}

function DashboardBody() {
  const { data } = useFetch("/dashboard");
  if (!data) return <div className="text-sm text-slate-500">Loading…</div>;
  const { pending, new_month: nm, new_today: nt, invoicing: iv, approvals: ap } = data;
  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="All values are basic values, excluding GST" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 mb-5">
        <Stat k="Pending orders" v={inr(pending.value)} sub={`${lms(pending.lms)} open, still to dispatch`} to="/orders?from=Pending+orders" testid="stat-pending-orders" />
        <Stat k="New LMs this month" v={inr(nm.value)} sub={`${lms(nm.lms)} · today ${inr(nt.value)} (${lms(nt.lms)})`} to="/orders" testid="stat-new-orders" />
        <Stat k="Invoicing planned today" v={inr(iv.today)} sub={`${lms(iv.today_lms)} · this month ${inr(iv.month)}`} to={`/orders?from=Invoicing+this+month&invoice_month=${new Date().toISOString().slice(0, 7)}`} testid="stat-invoicing-today" />
        <Stat k="Approvals pending" v={ap.lms} sub={`${inr(ap.value)} waiting for approval`} to="/approvals" tone="text-amber-700" testid="stat-pending-approvals" />
      </div>
      <Panel title="Recently added LMs" testid="dashboard-recent-orders">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {data.recent_orders.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 last:border-0">
                    <td className="py-2 pr-3 font-mono"><Link to={`/orders/${o.id}`} className="text-sky-700">{o.order_no}</Link></td>
                    <td className="py-2 pr-3">{o.customer_snapshot.name}</td>
                    <td className="py-2 pr-3 text-right font-mono">{inr(o.subtotal)}</td>
                    <td className="py-2 pr-3"><StatusBadge status={o.status} /></td>
                    <td className="py-2 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(o.created_at)}</td>
                  </tr>
                ))}
                {data.recent_orders.length === 0 && <tr><td className="py-6 text-center text-slate-500">No new LMs yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </Panel>
      <QuotePipeline quotes={data.quotes} />
      <BusinessPipeline />
    </div>
  );
}
